import { utcToZonedTime } from 'date-fns-tz';
import { DEFAULT_STORE_TIMEZONE } from 'features/store/constants';
import { Days } from 'features/store/types';
import {
  getCurrentDateInStoreTimeZone,
  getDateForHours,
  getNextDayClosingHours,
  getNextDayOpeningHours,
} from 'features/store/utils';

import { IRestaurant } from '@rbi-ctg/store';
import { TimedFireGenerateOptions } from 'state/launchdarkly/variations';

import { MINUTE_IN_MS, OPTION_NOW } from './constants';
import { ICalculateNextAvailableOptions, IOptionSettings } from './types';

/**
 * Calculates nearest 5-minute time slot in the future
 **/
export const getNearestTimeSlot = (store: IRestaurant): number => {
  const nowInStoreTimeZone = getCurrentDateInStoreTimeZone(store);

  let currentMinutes = nowInStoreTimeZone.getMinutes();

  while (currentMinutes % 5) {
    currentMinutes++;
  }

  nowInStoreTimeZone.setMinutes(currentMinutes);

  return nowInStoreTimeZone.getTime();
};

/**
 * Translates fireOrderIn, which is measured in seconds, to the nearest selectable option
 **/
export const deriveDefaultOptionFromValue = (
  value: number,
  options: IOptionSettings[]
): IOptionSettings | null => {
  if (!options?.length) {
    return null;
  }
  return options.reduce((a, b) => {
    return Math.abs(a.inSeconds - value) < Math.abs(b.inSeconds - value) ? a : b;
  });
};

/**
 * Dynamically generates an array of options for time picker
 * based on a time interval an offset.
 **/
export const generateOptions = ({
  timeInterval,
  length,
  offset = 0,
  startNow = false,
}: TimedFireGenerateOptions): IOptionSettings[] => {
  // indexPadding is used to modify the index to perform the correct calculation.
  // Defaults to 1 when neither {offset} nor {startNow} are set.
  let indexPadding = 1;

  if (startNow && offset) {
    // When both {startNow} and {offset} are set, then the 1st option is {OPTION_NOW},
    // the 2nd option is {offset}, and the 3rd option is {timeInterval + offset}.
    indexPadding = -1;
  } else if (startNow || offset) {
    // When either {startNow} or {offset} are set, the 1st option is {OPTION_NOW|offset}
    // and the 2nd option is {timeInterval + offset}.
    indexPadding = 0;
  }

  return Array.from({ length }, (a, i) => {
    if (startNow && i === 0) {
      return OPTION_NOW;
    }

    const minutes = (i + indexPadding) * timeInterval + offset;

    return {
      id: `${minutes}min`,
      inMinutes: minutes,
      inSeconds: minutes * 60,
      labelId: `${minutes}Min`,
      ariaLabel: { id: 'prepareInMinLabel' },
    };
  });
};

/**
 * Dynamically calculate an array of options for time picker when delivery is closed
 **/
export const calculateNextAvailableOptions = ({
  deliveryOptions,
  deliveryHours,
  now = new Date(),
  openingMinutesOffset = 45,
  storeTimeZone = DEFAULT_STORE_TIMEZONE,
}: ICalculateNextAvailableOptions): IOptionSettings[] => {
  const { timeInterval } = deliveryOptions;
  const nowInStoreTimeZone = utcToZonedTime(now.toISOString(), storeTimeZone);

  // Check if restaurant hasn't open yet or if
  // it's closed already to ensure we get the right hours
  const todayOpenString = `${Days[nowInStoreTimeZone.getDay() % 7]}Open`;
  const todayCloseString = `${Days[nowInStoreTimeZone.getDay() % 7]}Close`;
  const todayOpenHours = getDateForHours(deliveryHours[todayOpenString]);
  const todayCloseHours = getDateForHours(deliveryHours[todayCloseString]);

  const tomorrowOpenHours = getDateForHours(
    getNextDayOpeningHours(deliveryHours, now, storeTimeZone),
    storeTimeZone
  );

  const tomorrowCloseHours = getDateForHours(
    getNextDayClosingHours(deliveryHours, now, storeTimeZone),
    storeTimeZone
  );

  // Account for the different day as getDateForHours uses 'now' to create the date.
  tomorrowOpenHours.setDate(tomorrowOpenHours.getDate() + 1);
  tomorrowCloseHours.setDate(tomorrowCloseHours.getDate() + 1);

  const isClosedToday = now.getTime() > todayCloseHours.getTime();
  const open = isClosedToday ? tomorrowOpenHours : todayOpenHours;
  const close = isClosedToday ? tomorrowCloseHours : todayCloseHours;

  // Calculate the time to open
  const openTime = open.getTime() - now.getTime();

  // Calculate the props needed for the generateOptions function
  const offset = Math.ceil(openTime / MINUTE_IN_MS) + openingMinutesOffset;
  const length = Math.round((close.getTime() - open.getTime()) / MINUTE_IN_MS / timeInterval);

  return generateOptions({ offset, length, timeInterval });
};
